import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import USDHint from "../components/USDHint";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  RotateCcw,
  Plus,
  Search,
  Trash2,
  Printer,
  FileText,
  Package,
  Calendar,
  AlertTriangle,
  CheckCircle,
  MessageCircle,
  Filter,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const returnReasons = [
  { value: "defective", label: "منتج معيب" },
  { value: "wrong_item", label: "منتج خاطئ" },
  { value: "customer_changed_mind", label: "تغيير رأي العميل" },
  { value: "expired", label: "منتج منتهي الصلاحية" },
  { value: "damaged", label: "منتج تالف" },
  { value: "overstock", label: "زيادة في الطلب" },
  { value: "other", label: "سبب آخر" },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("ar-IQ").format(value || 0) + " د.ع";
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("ar-IQ", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ReturnsPage() {
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterReason, setFilterReason] = useState("all");
  const [dateFilter, setDateFilter] = useState({
    start: "",
    end: "",
  });

  // بيانات المرتجع الجديد
  const [newReturn, setNewReturn] = useState({
    customer_id: "",
    customer_name: "",
    reason: "",
    notes: "",
    return_to_inventory: true,
    items: [],
  });

  // إحصائيات
  const [stats, setStats] = useState({
    total_returns: 0,
    total_amount: 0,
    total_items: 0,
  });

  useEffect(() => {
    fetchReturns();
    fetchProducts();
    fetchCustomers();
  }, [dateFilter]);

  const fetchReturns = async () => {
    try {
      let url = `${API}/returns`;
      const params = new URLSearchParams();
      if (dateFilter.start) params.append("start_date", dateFilter.start);
      if (dateFilter.end) params.append("end_date", dateFilter.end);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url);
      setReturns(response.data);

      // حساب الإحصائيات
      const totalAmount = response.data.reduce((sum, r) => sum + (r.total_amount || 0), 0);
      const totalItems = response.data.reduce((sum, r) => sum + (r.items?.length || 0), 0);
      setStats({
        total_returns: response.data.length,
        total_amount: totalAmount,
        total_items: totalItems,
      });
    } catch (error) {
      console.error("Error fetching returns:", error);
      toast.error("فشل في تحميل المرتجعات");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleAddItem = () => {
    setNewReturn({
      ...newReturn,
      items: [
        ...newReturn.items,
        { product_id: "", product_name: "", quantity: 1, unit_price: 0, reason: newReturn.reason },
      ],
    });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = newReturn.items.filter((_, i) => i !== index);
    setNewReturn({ ...newReturn, items: updatedItems });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...newReturn.items];
    updatedItems[index][field] = value;

    // تحديث اسم المنتج والسعر عند اختيار المنتج
    if (field === "product_id") {
      const product = products.find((p) => p.id === value);
      if (product) {
        updatedItems[index].product_name = product.name;
        updatedItems[index].unit_price = product.sale_price || 0;
      }
    }

    setNewReturn({ ...newReturn, items: updatedItems });
  };

  const handleSubmit = async () => {
    if (!newReturn.reason) {
      toast.error("يرجى اختيار سبب الإرجاع");
      return;
    }
    if (newReturn.items.length === 0) {
      toast.error("يرجى إضافة منتج واحد على الأقل");
      return;
    }
    if (newReturn.items.some((item) => !item.product_id || item.quantity < 1)) {
      toast.error("يرجى إكمال بيانات جميع المنتجات");
      return;
    }

    try {
      // تحديث سبب كل منتج
      const itemsWithReason = newReturn.items.map((item) => ({
        ...item,
        reason: item.reason || newReturn.reason,
      }));

      const response = await axios.post(`${API}/returns`, {
        ...newReturn,
        items: itemsWithReason,
      });

      toast.success(`تم إنشاء المرتجع ${response.data.return_number} بنجاح`);
      setIsDialogOpen(false);
      resetForm();
      fetchReturns();
      fetchProducts(); // تحديث المخزون
    } catch (error) {
      console.error("Error creating return:", error);
      toast.error("فشل في إنشاء المرتجع");
    }
  };

  const handleDelete = async (returnId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المرتجع؟ سيتم إرجاع الكميات من المخزون.")) {
      return;
    }

    try {
      await axios.delete(`${API}/returns/${returnId}`);
      toast.success("تم حذف المرتجع بنجاح");
      fetchReturns();
      fetchProducts();
    } catch (error) {
      console.error("Error deleting return:", error);
      toast.error("فشل في حذف المرتجع");
    }
  };

  const resetForm = () => {
    setNewReturn({
      customer_id: "",
      customer_name: "",
      reason: "",
      notes: "",
      return_to_inventory: true,
      items: [],
    });
  };

  const printReturn = (ret) => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>وصل مرتجع - ${ret.return_number}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; max-width: 210mm; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #dc2626; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { color: #dc2626; font-size: 24px; margin-bottom: 5px; }
          .return-num { font-size: 18px; color: #666; }
          .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; padding: 10px; background: #fef2f2; border-radius: 8px; }
          .reason-box { background: #fee2e2; padding: 10px 15px; border-radius: 8px; margin-bottom: 20px; border-right: 4px solid #dc2626; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background: #dc2626; color: white; padding: 10px; text-align: right; }
          td { padding: 10px; border-bottom: 1px solid #e5e7eb; }
          .total-row { background: #fef2f2; font-weight: bold; }
          .total-amount { font-size: 20px; color: #dc2626; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px dashed #ccc; color: #666; font-size: 12px; }
          .signature { margin-top: 40px; display: flex; justify-content: space-between; }
          .signature div { text-align: center; }
          .signature-line { border-top: 1px solid #333; width: 150px; margin: 30px auto 5px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>وصل مرتجع</h1>
          <div class="return-num">${ret.return_number}</div>
        </div>
        
        <div class="info-section">
          <div>
            <strong>التاريخ:</strong> ${formatDate(ret.created_at)}
          </div>
          <div>
            <strong>العميل:</strong> ${ret.customer_name || "غير محدد"}
          </div>
        </div>
        
        <div class="reason-box">
          <strong>سبب الإرجاع:</strong> ${returnReasons.find(r => r.value === ret.reason)?.label || ret.reason}
          ${ret.notes ? `<br><strong>ملاحظات:</strong> ${ret.notes}` : ""}
        </div>
        
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>المنتج</th>
              <th>الكمية</th>
              <th>السعر</th>
              <th>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            ${ret.items.map((item, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${item.product_name}</td>
                <td>${item.quantity}</td>
                <td>${formatCurrency(item.unit_price)}</td>
                <td>${formatCurrency(item.quantity * item.unit_price)}</td>
              </tr>
            `).join("")}
            <tr class="total-row">
              <td colspan="4" style="text-align: left;"><strong>الإجمالي:</strong></td>
              <td class="total-amount">${formatCurrency(ret.total_amount)}</td>
            </tr>
          </tbody>
        </table>
        
        <div style="padding: 10px; background: ${ret.return_to_inventory ? '#dcfce7' : '#fef9c3'}; border-radius: 8px; text-align: center;">
          ${ret.return_to_inventory ? '✅ تم إرجاع الكميات للمخزون' : '⚠️ لم يتم إرجاع الكميات للمخزون'}
        </div>
        
        <div class="signature">
          <div>
            <div class="signature-line"></div>
            <span>توقيع المستلم</span>
          </div>
          <div>
            <div class="signature-line"></div>
            <span>توقيع المسؤول</span>
          </div>
        </div>
        
        <div class="footer">
          <p>Powered by Nazario-Hesabaty</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const shareViaWhatsApp = (ret) => {
    let text = `*وصل مرتجع*\n`;
    text += `${"═".repeat(20)}\n\n`;
    text += `🔢 رقم المرتجع: ${ret.return_number}\n`;
    text += `📅 التاريخ: ${formatDate(ret.created_at)}\n`;
    if (ret.customer_name) text += `👤 العميل: ${ret.customer_name}\n`;
    text += `\n📋 *سبب الإرجاع:* ${returnReasons.find(r => r.value === ret.reason)?.label || ret.reason}\n\n`;
    text += `📦 *المنتجات:*\n`;
    ret.items.forEach((item, i) => {
      text += `${i + 1}. ${item.product_name} × ${item.quantity} = ${formatCurrency(item.quantity * item.unit_price)}\n`;
    });
    text += `\n💰 *الإجمالي: ${formatCurrency(ret.total_amount)}*\n`;
    text += `\n${ret.return_to_inventory ? '✅ تم الإرجاع للمخزون' : '⚠️ لم يرجع للمخزون'}\n`;
    text += `\n${"═".repeat(20)}\n`;
    text += `_Powered by Nazario-Hesabaty_`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encodedText}`, "_blank");
  };

  // تصفية المرتجعات
  const filteredReturns = returns.filter((ret) => {
    const matchesSearch =
      ret.return_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesReason = filterReason === "all" || ret.reason === filterReason;
    return matchesSearch && matchesReason;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="returns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <RotateCcw className="w-7 h-7 text-red-500" />
            المرتجعات
          </h1>
          <p className="text-slate-400 text-sm mt-1">إدارة المنتجات المرتجعة وإرجاعها للمخزون</p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-red-600 hover:bg-red-700"
          data-testid="add-return-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          مرتجع جديد
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">عدد المرتجعات</p>
                <p className="text-2xl font-bold text-white">{stats.total_returns}</p>
              </div>
              <RotateCcw className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">إجمالي القيمة</p>
                <p className="text-2xl font-bold text-amber-400">{formatCurrency(stats.total_amount)}</p>
                <USDHint amount={stats.total_amount} />
              </div>
              <FileText className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">عدد المنتجات</p>
                <p className="text-2xl font-bold text-blue-400">{stats.total_items}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-slate-300 mb-2 block">بحث</Label>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="رقم المرتجع أو اسم العميل..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10 bg-slate-900 border-slate-700 text-white"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-slate-300 mb-2 block">السبب</Label>
              <Select value={filterReason} onValueChange={setFilterReason}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-white">الكل</SelectItem>
                  {returnReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value} className="text-white">
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-slate-300 mb-2 block">من تاريخ</Label>
              <Input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div className="w-40">
              <Label className="text-slate-300 mb-2 block">إلى تاريخ</Label>
              <Input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Returns Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-300">رقم المرتجع</TableHead>
                <TableHead className="text-slate-300">التاريخ</TableHead>
                <TableHead className="text-slate-300">العميل</TableHead>
                <TableHead className="text-slate-300">السبب</TableHead>
                <TableHead className="text-slate-300">المنتجات</TableHead>
                <TableHead className="text-slate-300">القيمة</TableHead>
                <TableHead className="text-slate-300">المخزون</TableHead>
                <TableHead className="text-slate-300 text-center">الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReturns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-10">
                    لا توجد مرتجعات
                  </TableCell>
                </TableRow>
              ) : (
                filteredReturns.map((ret) => (
                  <TableRow key={ret.id} className="border-slate-700 hover:bg-slate-700/30">
                    <TableCell className="font-mono text-red-400">{ret.return_number}</TableCell>
                    <TableCell className="text-slate-300">{formatDate(ret.created_at)}</TableCell>
                    <TableCell className="text-white">{ret.customer_name || "-"}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                        {returnReasons.find((r) => r.value === ret.reason)?.label || ret.reason}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-300">{ret.items?.length || 0}</TableCell>
                    <TableCell className="text-amber-400 font-bold">{formatCurrency(ret.total_amount)}</TableCell>
                    <TableCell>
                      {ret.return_to_inventory ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3" />
                          تم الإرجاع
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-400 text-xs">
                          <AlertTriangle className="w-3 h-3" />
                          لم يرجع
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => printReturn(ret)}
                          className="h-8 w-8 text-slate-400 hover:text-white"
                          title="طباعة"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => shareViaWhatsApp(ret)}
                          className="h-8 w-8 text-green-400 hover:text-green-300"
                          title="واتساب"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(ret.id)}
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Return Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-500" />
              إنشاء مرتجع جديد
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Customer Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">العميل (اختياري)</Label>
                <Select
                  value={newReturn.customer_id}
                  onValueChange={(value) => {
                    const customer = customers.find((c) => c.id === value);
                    setNewReturn({
                      ...newReturn,
                      customer_id: value,
                      customer_name: customer?.name || "",
                    });
                  }}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="اختر العميل" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id} className="text-white">
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">أو اكتب اسم العميل</Label>
                <Input
                  value={newReturn.customer_name}
                  onChange={(e) => setNewReturn({ ...newReturn, customer_name: e.target.value })}
                  placeholder="اسم العميل"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Reason Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">سبب الإرجاع *</Label>
              <Select
                value={newReturn.reason}
                onValueChange={(value) => setNewReturn({ ...newReturn, reason: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="reason-select">
                  <SelectValue placeholder="اختر السبب" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {returnReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value} className="text-white">
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Return to Inventory Toggle */}
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <input
                type="checkbox"
                id="returnToInventory"
                checked={newReturn.return_to_inventory}
                onChange={(e) => setNewReturn({ ...newReturn, return_to_inventory: e.target.checked })}
                className="w-4 h-4 accent-green-600"
              />
              <label htmlFor="returnToInventory" className="text-slate-200 flex items-center gap-2">
                <Package className="w-4 h-4 text-green-500" />
                إرجاع الكميات للمخزون
              </label>
            </div>

            {/* Products Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">المنتجات المرتجعة *</Label>
                <Button
                  type="button"
                  onClick={handleAddItem}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 me-1" />
                  إضافة منتج
                </Button>
              </div>

              {newReturn.items.length === 0 ? (
                <div className="text-center py-8 text-slate-500 border border-dashed border-slate-700 rounded-lg">
                  اضغط "إضافة منتج" لإضافة المنتجات المرتجعة
                </div>
              ) : (
                <div className="space-y-2">
                  {newReturn.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex-1">
                        <Select
                          value={item.product_id}
                          onValueChange={(value) => handleItemChange(index, "product_id", value)}
                        >
                          <SelectTrigger className="bg-slate-900 border-slate-700">
                            <SelectValue placeholder="اختر المنتج" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                            {products.map((product) => (
                              <SelectItem key={product.id} value={product.id} className="text-white">
                                {product.name} - {formatCurrency(product.sale_price)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", parseInt(e.target.value) || 1)}
                          placeholder="الكمية"
                          className="bg-slate-900 border-slate-700"
                        />
                      </div>
                      <div className="w-28 text-amber-400 text-sm">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}

                  {/* Total */}
                  <div className="flex justify-end p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                    <span className="text-slate-300 me-4">الإجمالي:</span>
                    <span className="text-xl font-bold text-red-400">
                      {formatCurrency(newReturn.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-slate-300">ملاحظات</Label>
              <textarea
                value={newReturn.notes}
                onChange={(e) => setNewReturn({ ...newReturn, notes: e.target.value })}
                placeholder="أضف ملاحظات إضافية..."
                className="w-full bg-slate-800 border border-slate-700 rounded-md p-3 text-white min-h-[80px] resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                resetForm();
              }}
              className="border-slate-600 text-slate-300"
            >
              إلغاء
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-return-btn"
            >
              <CheckCircle className="w-4 h-4 me-2" />
              إنشاء المرتجع
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
